import {
  Binding,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Type,
  ViewContainerRef,
} from '@angular/core';

import { ChdSnackbarService, ChdSnackbarType } from '@cp-produkte/cruise-hospitality-design';
import { Observable, take } from 'rxjs';

import { ModalButton, NotificationModalComponent } from './notification/modal/notification-modal.component';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly injector = inject(EnvironmentInjector);
  private readonly snackbar = inject(ChdSnackbarService);

  private vcr?: ViewContainerRef;

  init(vcr: ViewContainerRef) {
    if (this.vcr) {
      throw new Error('ToastService already initialized');
    }
    this.vcr = vcr;
  }

  showToast(config: NotificationConfig): void {
    this.createNotification(config);
  }

  showModal(config: ModalConfig): void {
    this.createModal(config);
  }

  private createNotification(config: NotificationConfig): void {
    const { type = 'default', title = '', message, duration = 5 } = config;
    this.snackbar.open(message, {
      title: title || undefined,
      type: NOTIFICATION_TYPE_TO_CHD_TYPE[type],
      duration: duration * 1000,
    });
  }

  private createModal(config: ModalConfig): void {
    if (!this.vcr) {
      throw new Error('ToastService not initialized');
    }

    // Create component
    const componentRef = this.createComponentRef(config.comp ?? NotificationModalComponent, {
      environmentInjector: this.injector,
      bindings: config.bindings,
    });

    // Set input properties
    Object.entries(config.data || {}).forEach(([key, value]) => {
      componentRef.setInput(key, value);
    });

    componentRef.changeDetectorRef.detectChanges();

    this.vcr.insert(componentRef.hostView);

    // Clean up
    componentRef.instance.close.pipe(take(1)).subscribe(() => this.removeModal(componentRef));
  }

  private removeModal(componentRef: ComponentRef<ModalComponent>): void {
    this.vcr?.remove(this.vcr?.indexOf(componentRef.hostView));
  }

  private createComponentRef<C>(component: Type<C>, options: Parameters<typeof createComponent>[1]): ComponentRef<C> {
    return createComponent(component, options);
  }
}

export type NotificationType = 'success' | 'error' | 'default';

/** Maps the app's notification severities onto Chd snackbar types. */
const NOTIFICATION_TYPE_TO_CHD_TYPE: Record<NotificationType, ChdSnackbarType> = {
  success: 'success',
  error: 'error',
  default: 'info',
};

export interface NotificationConfig {
  type?: NotificationType;
  title?: string;
  message: string;
  duration?: number;
}

export interface ModalConfig {
  comp?: Type<ModalComponent>;
  data?: ModalData;
  bindings?: Binding[];
}

export interface ModalData {
  titleString?: string;
  messages?: string | string[];
  buttons?: ModalButton[];
  [key: string]: unknown;
}

export interface ModalComponent {
  close: Observable<void>;
}
